import { createSlice } from "@reduxjs/toolkit";
import storage from "web/script/utils/storage";
import environment from "web/script/modules/environment";

export interface EventCounterSlice {
    shouldBlockFilter: boolean;
    shouldBlockAll: boolean;
    shouldBlockSemiHard: boolean;
    shouldBlockSemiSoft: boolean;
}

function readFromStorage(limit): boolean {
    const blockVariant = environment.getFeature("mg_block_on_event_counter");
    const eventCounter = storage.get("eventCounter", null, true);
    if (!eventCounter) {
        return false;
    }
    if (eventCounter.eventCount >= limit) {
        if (
            blockVariant === "hard_block" ||
            (blockVariant === "soft_block" && !eventCounter.hasBeenDismissed)
        ) {
            return true;
        } else if (
            environment.getFeature("mg_second_interaction_product_card_soft_block") ||
            environment.getFeature("mg_semi_hard_block_feeds_show_more")
        ) {
            return true;
        }
        return false;
    }
    return false;
}

function updateStorage({ category, action }): void {
    // eventCounter tracks whether a block has been dismissed and how many
    // valid events have occurred this session
    const eventCounter = storage.get("eventCounter", null, true)
        ? storage.get("eventCounter", null, true)
        : {
              eventCount: 0,
              hasBeenDismissed: false,
          };
    // 5 is an arbitrary limit to count to because it's higher than any limit needed thus far
    // for filter click dont count the event on "filter close"
    if (
        (category === "app_overlay" && action === "modal_dismissed") ||
        (category === "send_to_app" && action === "continue_clicked")
    ) {
        eventCounter.hasBeenDismissed = true;
    } else if (
        eventCounter.eventCount < 5 &&
        (category !== "filter" || (category === "filter" && action !== "open"))
    ) {
        eventCounter.eventCount++;
    }

    // on every valid event update local storage
    storage.set("eventCounter", eventCounter, true);
}

function flagEnabled(flag): boolean {
    if (flag === "mg_block_on_event_counter") {
        return environment.getFeature("mg_block_on_event_counter") !== "no_block";
    } else if (flag === "mg_semi_hard_block_feeds_show_more") {
        return Boolean(environment.getFeature("mg_semi_hard_block_feeds_show_more"));
    } else if (flag === "mg_second_interaction_product_card_soft_block") {
        return Boolean(environment.getFeature("mg_second_interaction_product_card_soft_block"));
    }
    return false;
}

const eventCountTracker = createSlice({
    name: "eventCounter",
    initialState: {
        shouldBlockFilter: readFromStorage(2) && flagEnabled("mg_block_on_event_counter"),
        shouldBlockAll: readFromStorage(3) && flagEnabled("mg_block_on_event_counter"),
        shouldBlockSemiHard:
            readFromStorage(1) && flagEnabled("mg_semi_hard_block_feeds_show_more"),
        shouldBlockSemiSoft:
            readFromStorage(1) && flagEnabled("mg_second_interaction_product_card_soft_block"),
    } as EventCounterSlice,
    reducers: {
        registerEvent(state, { payload }): void {
            updateStorage(payload);
            state.shouldBlockAll = readFromStorage(3) && flagEnabled("mg_block_on_event_counter");
            state.shouldBlockFilter =
                readFromStorage(2) && flagEnabled("mg_block_on_event_counter");
            state.shouldBlockSemiHard =
                readFromStorage(1) && flagEnabled("mg_semi_hard_block_feeds_show_more");
            state.shouldBlockSemiSoft =
                readFromStorage(2) && flagEnabled("mg_second_interaction_product_card_soft_block");
        },
    },
});

export const { registerEvent } = eventCountTracker.actions;
export default eventCountTracker.reducer;
